import type {ClientTool, ClientToolCall} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {
  ActionType,
  SessionActiveClient,
  ToolCallResult,
  ToolResultTextContent,
} from '@microsoft/agent-host-protocol'

export const SESSION_TITLE_TOOL_NAME = 'set_session_title'
export const SESSION_TITLE_MAX_LENGTH = 40

export const ACTION_SESSION_TITLE_CHANGED = 'session/titleChanged' as ActionType.SessionTitleChanged

export interface SessionTitleClientToolDeps {
  /** Dispatch a `session/titleChanged` for the enclosing task's session. */
  renameSession: (title: string) => void
}

const SESSION_TITLE_TOOL_DEFINITION: ClientTool['definition'] = {
  name: SESSION_TITLE_TOOL_NAME,
  title: 'Set session title',
  description: 'Sets a short, descriptive title for the current chat session.',
  inputSchema: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        description: 'Short, descriptive session title, ideally 1–4 words',
        maxLength: SESSION_TITLE_MAX_LENGTH,
      },
    },
    required: ['title'],
  },
  outputSchema: {
    type: 'object',
    properties: {
      title: {type: 'string'},
    },
    required: ['title'],
  },
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
  },
}

/**
 * Active-client customization for the Quick Chat title flow. Only carries the
 * `displayName`; the runtime merges the actual tool definition from
 * {@link buildSessionTitleClientTool} into the same publication (see
 * `RelayConnectionRuntime#buildActiveClient`), so both sources land in the
 * daemon's frozen create-time tool set.
 */
export function buildSessionTitleActiveClient(clientId: string): SessionActiveClient {
  return {
    clientId,
    displayName: 'GitHub Quick Chat',
    tools: [],
  }
}

/**
 * `ClientTool` implementation of `set_session_title`. Registered through
 * `RelayConnectionRuntimeOptions.clientTools`, so the relay runtime owns
 * routing and completion — a single path completes each call, avoiding the
 * duplicate success/failure completions that would arise if a separate
 * scanner also called `completeToolCall`.
 */
export function buildSessionTitleClientTool(deps: SessionTitleClientToolDeps): ClientTool {
  return {
    definition: SESSION_TITLE_TOOL_DEFINITION,
    fulfil: call => fulfilSessionTitle(call, deps),
  }
}

function fulfilSessionTitle(call: ClientToolCall, deps: SessionTitleClientToolDeps): ToolCallResult {
  try {
    const title = parseSessionTitleToolInput(call.toolInput)
    deps.renameSession(title)
    return {
      success: true,
      pastTenseMessage: `Set session title to ${title}`,
      structuredContent: {title},
      content: [textToolContent(`Set session title to ${title}`)],
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return {
      success: false,
      pastTenseMessage: 'Failed to set session title',
      error: {message},
      content: [textToolContent(message)],
    }
  }
}

export function parseSessionTitleToolInput(toolInput: string | undefined): string {
  if (!toolInput) throw new Error(`${SESSION_TITLE_TOOL_NAME} requires a non-empty title string.`)

  let parsed: unknown
  try {
    parsed = JSON.parse(toolInput)
  } catch {
    throw new Error(`${SESSION_TITLE_TOOL_NAME} requires JSON input.`)
  }

  if (!parsed || typeof parsed !== 'object' || !('title' in parsed)) {
    throw new Error(`${SESSION_TITLE_TOOL_NAME} requires a non-empty title string.`)
  }
  const title = (parsed as {title?: unknown}).title
  if (typeof title !== 'string' || !title.trim()) {
    throw new Error(`${SESSION_TITLE_TOOL_NAME} requires a non-empty title string.`)
  }

  return truncateByCodePoint(title.trim(), SESSION_TITLE_MAX_LENGTH)
}

/**
 * Trim a string to at most `max` Unicode code points. `String#slice` counts
 * UTF-16 code units and can split an emoji surrogate pair, producing a lone
 * surrogate that renders as U+FFFD. `Array.from` iterates by code point (via
 * the string iterator protocol), so slicing that array preserves whole
 * characters and matches JSON-Schema `maxLength`.
 */
export function truncateByCodePoint(value: string, max: number): string {
  const codePoints = Array.from(value)
  if (codePoints.length <= max) return value
  return codePoints.slice(0, max).join('')
}

function textToolContent(text: string): ToolResultTextContent {
  return {type: 'text', text} as ToolResultTextContent
}
